/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  AbstractRestService,
  arrayOf,
  Enum,
  identity,
  none,
  optimisticLockingOf,
  RestSettings,
} from '@gh/core-data';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  LetterTemplateCopyDto,
  LetterTemplateDto,
  LetterTemplateSourceDto,
  LetterTemplateType,
  LetterTemplateValidationResultDto,
} from './letter-template.dto';
import {
  LetterTemplateCopyMapper,
  LetterTemplateMapper,
  LetterTemplateSourceMapper,
} from './letter-template.mapper';

@Injectable()
export class LetterTemplateRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  copy(dto: LetterTemplateCopyDto): Observable<LetterTemplateDto> {
    return this.httpPost(
      `${this.bp}/letter-templates`,
      dto,
      optimisticLockingOf(LetterTemplateCopyMapper),
      optimisticLockingOf(LetterTemplateMapper));
  }

  pushDown(dto: LetterTemplateSourceDto): Observable<void> {
    return this.httpDelete(
      `${this.bp}/letter-templates/push-down`,
      dto,
      optimisticLockingOf(LetterTemplateSourceMapper),
      none());
  }

  validate(dto: LetterTemplateSourceDto): Observable<LetterTemplateValidationResultDto> {
    return this.httpPost(
      `${this.bp}/letter-templates/validate`,
      dto,
      optimisticLockingOf(LetterTemplateSourceMapper),
      identity()).pipe(
      map$(({ INCORRECT_FORMAT, MISSING_IN_DB, PARSING_FAILED, UNAVAILABLE_FOR_TEMPLATE }) => ({
        incorrectFormat: INCORRECT_FORMAT,
        missingInDb: MISSING_IN_DB,
        parsingFailed: PARSING_FAILED,
        unavailableForTemplate: UNAVAILABLE_FOR_TEMPLATE,
      })));
  }

  findAll(type: LetterTemplateType): Observable<LetterTemplateDto[]> {
    return this.httpGet(
      `${this.bp}/letter-templates`, {
        type: Enum.toValue(LetterTemplateType, type),
      },
      void 0,
      arrayOf(LetterTemplateMapper));
  }
}
